import classNames from "classnames/bind";
import React, {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type ReactElement,
} from "react";
import Markdown, {
  type AllowElement,
  type Components,
  type Options as ReactMarkdownOptionsType,
} from "react-markdown";
import remarkFlexibleMarkersPlugin from "remark-flexible-markers";
import remarkMathPlugin from "remark-math";
import { colours } from "../../constants/colours";
import { Icon } from "../../Icon";
import { DelayedContentReveal } from "../../SequentialContentReveal/SequentialContentReveal";
import { getTextFromReactNode } from "../../utils/dom";
import { MathDataDisplay } from "../MathDataDisplay";
import { DataDisplayWithAdornment } from "../_components/DataDisplayWithAdornment";
import styles from "./RichTextDataDisplay.module.scss";
import type { RichTextMarkdownDataDisplayProps } from "./types";

const cx = classNames.bind(styles);
const richTextMarkdownMathBlockDataAttribute =
  "data-validere-rich-text-md-math-block" as const;

const getNormalizedMarkdownCodeClassName = (className: unknown): string => {
  if (Array.isArray(className)) {
    return className.filter(Boolean).join(" ");
  }

  return typeof className === "string" ? className : "";
};

type MarkdownBlockComponentPropsType = {
  children?: React.ReactNode;
};

const RichTextMarkdownDataDisplay = ({
  value,
  isNextSizing = false,
  isContainerSizing = false,
  sequentialReveal = false,
  sequentialRevealSpeed = 250,
  onSequentialRevealComplete,
  isConstrainedToParentWidth = false,
  alignment = "left",
  emptyContent = "",
  showInvalidIndicator = true,
  invalidText = "Unsupported data type",
  className,
  style,
  isInline = false,
  isBusy = false,
  isLoading = false,
  as: asProp,
}: RichTextMarkdownDataDisplayProps) => {
  const rootEl = useRef<Parameters<AllowElement>[2]>();
  const counterRef = useRef(1);
  const resetTimerRef = useRef<NodeJS.Timeout>();
  const animationCompleteTimerRef = useRef<NodeJS.Timeout>();
  const isAnimating = useRef(false);
  const isAnimationComplete = useRef(false);

  const getCounter = useCallback(() => {
    // if a reset timer exists already, clear it
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
    }

    if (animationCompleteTimerRef.current) {
      clearTimeout(animationCompleteTimerRef.current);
    }

    // kick off a timer that will reset the counter back to 1 unless called again in 250ms
    resetTimerRef.current = setTimeout(() => {
      counterRef.current = 1;
    }, sequentialRevealSpeed);

    animationCompleteTimerRef.current = setTimeout(() => {
      if (!isAnimating.current) return;

      isAnimationComplete.current = true;
      onSequentialRevealComplete?.();
    }, sequentialRevealSpeed + 1500);

    // return the counter + 1
    isAnimating.current = true;
    return counterRef.current++;
  }, [sequentialRevealSpeed]);

  // clear off the reset timer on unmount
  useEffect(() => {
    return () => {
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      if (animationCompleteTimerRef.current)
        clearTimeout(animationCompleteTimerRef.current);
    };
  }, []);

  const isEmpty = value === null || value === "" || value === undefined;
  const isInvalid = value === null;
  const CustomNode = asProp ?? "div";
  const mayContainTexMathBlocks =
    typeof value === "string" && value.includes("$$");
  const markdownComponents = useMemo(() => {
    const wrapSequentialReveal = (node: React.ReactNode) => {
      if (!sequentialReveal) {
        return <>{node}</>;
      }

      return (
        <DelayedContentReveal
          delay={getCounter() * sequentialRevealSpeed}
          hiddenRenderBeforeDelay
        >
          <div className={cx("animate")}>{node}</div>
        </DelayedContentReveal>
      );
    };

    const mathComponents = {
      code: ({
        className,
        children,
        ...rest
      }: ComponentPropsWithoutRef<"code">) => {
        const classNameNormalized =
          getNormalizedMarkdownCodeClassName(className);

        if (!classNameNormalized.includes("language-math")) {
          return (
            <code
              className={className}
              {...rest}
            >
              {children}
            </code>
          );
        }

        const latex = getTextFromReactNode(children).replace(/\n$/, "").trim();

        if (!latex) {
          return null;
        }

        const mathTree = (
          <MathDataDisplay
            className={cx(
              "markdown-math-container",
              isConstrainedToParentWidth &&
                "markdown-math-container--fill-width"
            )}
          >
            <MathDataDisplay.Group type="tex">{latex}</MathDataDisplay.Group>
          </MathDataDisplay>
        );

        return (
          <span
            {...{ [richTextMarkdownMathBlockDataAttribute]: "" }}
            className={cx("markdown-math-wrapper")}
          >
            {wrapSequentialReveal(mathTree)}
          </span>
        );
      },
      pre: ({ children, ...rest }: ComponentPropsWithoutRef<"pre">) => {
        const childList = Children.toArray(children);
        const onlyChild = childList[0];

        if (
          childList.length === 1 &&
          isValidElement(onlyChild) &&
          typeof onlyChild.props === "object" &&
          onlyChild.props !== null &&
          richTextMarkdownMathBlockDataAttribute in onlyChild.props
        ) {
          return (
            <>
              {
                (onlyChild as ReactElement<{ children?: React.ReactNode }>)
                  .props.children
              }
            </>
          );
        }

        return <pre {...rest}>{children}</pre>;
      },
    };

    if (!sequentialReveal) {
      return mathComponents;
    }

    return {
      ...mathComponents,
      h1: ({ children }: MarkdownBlockComponentPropsType) => {
        return (
          <DelayedContentReveal
            delay={getCounter() * sequentialRevealSpeed}
            hiddenRenderBeforeDelay
          >
            <h1 className={cx("animate")}>{children}</h1>
          </DelayedContentReveal>
        );
      },
      h2: ({ children }: MarkdownBlockComponentPropsType) => {
        return (
          <DelayedContentReveal
            delay={getCounter() * sequentialRevealSpeed}
            hiddenRenderBeforeDelay
          >
            <h2 className={cx("animate")}>{children}</h2>
          </DelayedContentReveal>
        );
      },
      h3: ({ children }: MarkdownBlockComponentPropsType) => (
        <DelayedContentReveal
          delay={getCounter() * sequentialRevealSpeed}
          hiddenRenderBeforeDelay
        >
          <h3 className={cx("animate")}>{children}</h3>
        </DelayedContentReveal>
      ),
      p: ({ children }: MarkdownBlockComponentPropsType) => (
        <DelayedContentReveal
          delay={getCounter() * sequentialRevealSpeed}
          hiddenRenderBeforeDelay
        >
          <p className={cx("animate")}>{children}</p>
        </DelayedContentReveal>
      ),
      ul: ({ children }: MarkdownBlockComponentPropsType) => (
        <DelayedContentReveal
          delay={getCounter() * sequentialRevealSpeed}
          hiddenRenderBeforeDelay
        >
          <ul className={cx("animate")}>{children}</ul>
        </DelayedContentReveal>
      ),
      li: ({ children }: MarkdownBlockComponentPropsType) => {
        return (
          <DelayedContentReveal
            delay={getCounter() * sequentialRevealSpeed}
            hiddenRenderBeforeDelay
          >
            <li className={cx("animate")}>{children}</li>
          </DelayedContentReveal>
        );
      },
      ol: ({ children }: MarkdownBlockComponentPropsType) => (
        <DelayedContentReveal
          delay={getCounter() * sequentialRevealSpeed}
          hiddenRenderBeforeDelay
        >
          <ol className={cx("animate")}>{children}</ol>
        </DelayedContentReveal>
      ),
    };
  }, [
    getCounter,
    isConstrainedToParentWidth,
    sequentialReveal,
    sequentialRevealSpeed,
  ]) as Components;
  const remarkPlugins = useMemo((): NonNullable<
    ReactMarkdownOptionsType["remarkPlugins"]
  > => {
    if (!mayContainTexMathBlocks) {
      return [remarkFlexibleMarkersPlugin];
    }

    return [
      [remarkMathPlugin, { singleDollarTextMath: false }],
      remarkFlexibleMarkersPlugin,
    ];
  }, [mayContainTexMathBlocks]);
  const markdownElement = (
    <Markdown
      remarkPlugins={remarkPlugins}
      allowElement={(_, __, parent) => {
        if (!rootEl.current && parent?.type === "root") rootEl.current = parent;

        return true;
      }}
      components={markdownComponents}
    >
      {value}
    </Markdown>
  );

  return (
    <DataDisplayWithAdornment
      showAdornment={showInvalidIndicator && !isEmpty && isInvalid}
      alignment={alignment}
      isInline={isInline}
      isBusy={isBusy}
      isLoading={isLoading}
      adornmentSlot={({ iconProps, className }) => (
        <span className={className}>
          <Icon
            {...iconProps}
            variant="warning"
            colour={colours.status.warning}
            alt={invalidText}
            aria-roledescription="Warning Icon"
          />
        </span>
      )}
      className={cx(className, isInvalid && "bare")}
      style={style}
    >
      {isEmpty || isInvalid ? (
        emptyContent
      ) : (
        <CustomNode
          className={cx("container", {
            "is-next-sizing": isNextSizing,
            "is-container-sizing": isContainerSizing,
            "is-constrained-to-parent-width": isConstrainedToParentWidth,
          })}
        >
          {mayContainTexMathBlocks ? (
            <MathDataDisplay.Root
              type="tex"
              isLoading={isLoading}
            >
              {markdownElement}
            </MathDataDisplay.Root>
          ) : (
            markdownElement
          )}
        </CustomNode>
      )}
    </DataDisplayWithAdornment>
  );
};

export default RichTextMarkdownDataDisplay;
